import { Paging } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { StoreInjection } from "features/storeManagement/di/StoreInjection";
import { StoreFormRequestEntity } from "features/storeManagement/domain/entities/StoreFormRequestEntity";
import { StoreRequestEntity } from "features/storeManagement/domain/entities/StoreRequestEntity";
import { StoreResponseEntity } from "features/storeManagement/domain/entities/StoreResponseEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";

// ─── Input ────────────────────────────────────────────────────────────────────

class StoreInput implements Disposable {
  public fetchList = new BehaviorSubject<StoreRequestEntity>({
    page: 0,
    limit: 10,
  });
  public fetchDetail = new BehaviorSubject<string>("");
  public fetchPartner = new BehaviorSubject<BaseFilterListRequestModel>({});
  public createStore = new BehaviorSubject<StoreFormRequestEntity>({});
  public updateStore = new BehaviorSubject<StoreFormRequestEntity>({});
  public deleteStore = new BehaviorSubject<string>("");

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchDetail.complete();
    this.fetchPartner.complete();
    this.createStore.complete();
    this.updateStore.complete();
    this.deleteStore.complete();
  }
}

// ─── Output ───────────────────────────────────────────────────────────────────

class StoreOutput implements Disposable {
  public savedList = new BehaviorSubject<Array<StoreResponseEntity>>([]);
  public savedDetail = new BehaviorSubject<StoreResponseEntity | null>(null);
  public savedPartner = new BehaviorSubject<Array<BaseFilterListResponseModel>>([]);
  public pagination = new BehaviorSubject<Paging>({
    page: 0,
    limit: 10,
    total: 0,
  });
  public onError = new BehaviorSubject<string>("");
  public actionSuccess = new BehaviorSubject<"create" | "update" | "delete" | null>(null);
  public listLoading = new BehaviorSubject<boolean>(false);
  public formLoading = new BehaviorSubject<boolean>(false);
  public detailLoading = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.savedDetail.complete();
    this.savedPartner.complete();
    this.pagination.complete();
    this.onError.complete();
    this.actionSuccess.complete();
    this.listLoading.complete();
    this.formLoading.complete();
    this.detailLoading.complete();
  }
}

// ─── ViewModel ────────────────────────────────────────────────────────────────

@injectable()
export class StoreManagementViewModel extends BaseViewModel<
  StoreInput,
  StoreOutput
> {
  constructor() {
    super(new StoreInput(), new StoreOutput());
  }

  private useCase = StoreInjection.getUseCase();

  protected override transform(): void {
    // Fetch store list
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.output.listLoading.next(true);
        const res = await this.useCase.getStores(params);
        if (res?.data) {
          this.output.savedList.next(res.data);
          this.output.pagination.next(res.pagination);
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.listLoading.next(false);
      }
    });

    // Fetch store detail
    this.skipFirstInitial(this.input.fetchDetail, async (code) => {
      if (!code) return;
      try {
        this.output.detailLoading.next(true);
        const res = await this.useCase.getStoreDetail(code);
        if (res?.data) {
          this.output.savedDetail.next(res.data);
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.detailLoading.next(false);
      }
    });

    // Fetch partner list (FilterUseCase via UseCase)
    this.skipFirstInitial(this.input.fetchPartner, async (params) => {
      try {
        const res = await this.useCase.getPartnerList(params);
        if (res) {
          this.output.savedPartner.next(res);
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      }
    });

    // Create store
    this.skipFirstInitial(this.input.createStore, async (body) => {
      if (!body || Object.keys(body).length === 0) return;
      try {
        this.output.formLoading.next(true);
        const res = await this.useCase.createStore(body);
        if (res?.data) {
          this.output.actionSuccess.next("create");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.formLoading.next(false);
      }
    });

    // Update store
    this.skipFirstInitial(this.input.updateStore, async (body) => {
      if (!body || Object.keys(body).length === 0) return;
      try {
        this.output.formLoading.next(true);
        const res = await this.useCase.updateStore(body);
        if (res?.data) {
          this.output.actionSuccess.next("update");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.formLoading.next(false);
      }
    });

    // Delete store
    this.skipFirstInitial(this.input.deleteStore, async (code) => {
      if (!code) return;
      try {
        this.output.formLoading.next(true);
        const res = await this.useCase.deleteStore(code);
        if (res?.data) {
          this.output.actionSuccess.next("delete");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.formLoading.next(false);
      }
    });
  }
}
